/**
 * Favourites store — user-saved pattern configurations (brief §3.4).
 *
 * Free users may keep one favourite; pro is unlimited. The configure/preview
 * flow is always available — the limit applies only at save time, surfaced via
 * the replace flow (brief §5.3).
 */
import type { ArtistTile, LatticeParams, PatternConfig, WallpaperGroupId } from '@/patterns/wallpaper';
import { getFavouritesRepository } from '@/services/favouritesRepository';
import { useEntitlementStore } from '@/stores/entitlement';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export type NewFavourite = {
  label: string;
  wpgId: WallpaperGroupId;
  latticeParams: LatticeParams;
  artistTile?: ArtistTile;
};

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `fav-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useFavouritesStore = defineStore('favourites', () => {
  const favourites = ref<PatternConfig[]>([]);
  const loaded = ref(false);
  const repo = getFavouritesRepository();

  /** Free: one favourite. Pro: unlimited. */
  const canAddMore = computed(() => {
    const entitlement = useEntitlementStore();
    return entitlement.isPro || favourites.value.length === 0;
  });

  async function refresh(): Promise<void> {
    favourites.value = (await repo.load()).map((c) => ({ ...c, isSystem: false }));
    loaded.value = true;
  }

  async function persist(): Promise<void> {
    await repo.save(favourites.value);
  }

  /** Add a favourite. Returns the created config, or null if blocked by tier. */
  async function add(config: NewFavourite): Promise<PatternConfig | null> {
    if (!canAddMore.value) return null;
    const created: PatternConfig = {
      ...config,
      id: newId(),
      artistTile: config.artistTile ?? 'standard',
      isSystem: false,
    };
    favourites.value = [...favourites.value, created];
    await persist();
    return created;
  }

  /**
   * Replace the single existing favourite (free-tier replace flow, §5.3).
   * Removes all current favourites and adds the new one.
   */
  async function replace(config: NewFavourite): Promise<PatternConfig> {
    const created: PatternConfig = {
      ...config,
      id: newId(),
      artistTile: config.artistTile ?? 'standard',
      isSystem: false,
    };
    favourites.value = [created];
    await persist();
    return created;
  }

  /**
   * Overwrite an existing favourite in place (tweak-then-heart-again flow) —
   * keeps the same id, doesn't touch the tier count.
   */
  async function update(id: string, config: NewFavourite): Promise<PatternConfig | null> {
    const idx = favourites.value.findIndex((f) => f.id === id);
    if (idx === -1) return null;
    const updated: PatternConfig = {
      ...config,
      id,
      artistTile: config.artistTile ?? 'standard',
      isSystem: false,
    };
    favourites.value = favourites.value.map((f, i) => (i === idx ? updated : f));
    await persist();
    return updated;
  }

  async function remove(id: string): Promise<void> {
    favourites.value = favourites.value.filter((f) => f.id !== id);
    await persist();
  }

  async function rename(id: string, label: string): Promise<void> {
    favourites.value = favourites.value.map((f) => (f.id === id ? { ...f, label } : f));
    await persist();
  }

  return { favourites, loaded, canAddMore, refresh, add, replace, update, remove, rename, persist };
});
